/**
 * lib/pastelAgeAnalysis.ts
 * ------------------------------------------------------------------
 * FINANCE — reading Pastel's "Customer Age Analysis" and setting it
 * beside what the portal says each pupil owes.
 *
 * Pastel is still where the bursary keeps the books, so for now there
 * are two ledgers for the same pupils. The weekly comparison is how the
 * school finds out they disagree before a parent does: a payment
 * captured in one and not the other, a credit carried twice, a pupil
 * Pastel knows and the portal does not.
 *
 * The export, as Pastel writes it to Excel:
 *
 *   Livingstone Kolobeng College | Prepared by: …
 *   Customer Age Analysis for Monthly Customers as at 31/12/26
 *   Name | | | 120+ Days | 90 Days | 60 Days | 30 Days | Current | Total Due
 *   Category : 1 - Day School- Form 1
 *   BAL851 - BALEBENG AFILWE LOLLITAH | * | | 3000 | 0 | 0 | 0 | 0 | 3000
 *   …
 *   Total for Category
 *   Totals :
 *
 * or the short form with only `Name | Total Due`. Columns are found by
 * their header, so either reads. Positive = the customer owes, negative =
 * in credit — the same sign as `accountStanding().balance`, so the two
 * subtract directly.
 *
 * The "as at" date Pastel prints is the period end it was asked for
 * (often 31/12), not the day it was run, so it is kept as text and never
 * trusted as the comparison date.
 *
 * Matching is by `Student.pastelCode`, recorded by the August
 * reconciliation. A row whose code is on no pupil falls back to an exact
 * name match and says so — it is a lead, not a link.
 * ------------------------------------------------------------------ */

export interface AgeBuckets {
  days120: number;
  days90: number;
  days60: number;
  days30: number;
  current: number;
}

export interface PastelRow {
  code: string;
  name: string;
  /** Pastel customer category, e.g. "1 - Day School- Form 1". */
  category: string;
  totalDue: number;
  /** Present only when the export carried the ageing columns. */
  buckets?: AgeBuckets;
  /** Entries this year, when the row came from the Detailed Ledger (lib/pastelLedger). */
  activity?: number;
}

export interface PastelAgeAnalysis {
  /** Pastel's own heading, e.g. "… as at 31/12/26". Text only. */
  title: string;
  rows: PastelRow[];
  /** Σ totalDue of the rows read. */
  total: number;
  hasBuckets: boolean;
  warnings: string[];
}

function txt(v: unknown): string {
  return v === null || v === undefined ? '' : String(v).trim();
}

function num(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  const s = txt(v).replace(/[,\sP]/g, '');
  if (!s) return null;
  // Accounting negatives: "(1 000.00)"
  const neg = /^\(.*\)$/.test(s);
  const n = Number(s.replace(/[()]/g, ''));
  if (!Number.isFinite(n)) return null;
  return neg ? -n : n;
}

const round2 = (n: number) => Math.round(n * 100) / 100;

/** True when a sheet looks like a Pastel customer age analysis. */
export function isPastelAgeAnalysis(rows: unknown[][]): boolean {
  return rows.some((r) => r?.some((c) => /^Total Due$/i.test(txt(c))))
    && rows.some((r) => /^[A-Z0-9]{2,10}\s+-\s+\S/i.test(txt(r?.[0])));
}

export function parsePastelAgeAnalysis(rows: unknown[][]): PastelAgeAnalysis {
  const headerIdx = rows.findIndex((r) => r?.some((c) => /^Total Due$/i.test(txt(c))));
  if (headerIdx < 0) throw new Error('No "Total Due" column — this is not a Pastel customer age analysis.');
  const header = rows[headerIdx].map((c) => txt(c).toLowerCase());
  const col = (re: RegExp) => header.findIndex((h) => re.test(h));
  const iTotal = col(/^total due$/);
  const iB = {
    days120: col(/^120\+?\s*days$/),
    days90: col(/^90\s*days$/),
    days60: col(/^60\s*days$/),
    days30: col(/^30\s*days$/),
    current: col(/^current$/),
  };
  const hasBuckets = Object.values(iB).every((i) => i >= 0);
  const title = rows.slice(0, headerIdx).map((r) => txt(r?.[0])).find((t) => /age analysis/i.test(t)) ?? '';

  const out: PastelRow[] = [];
  const warnings: string[] = [];
  const seen = new Set<string>();
  let category = '';
  let footerTotal: number | null = null;
  for (let r = headerIdx + 1; r < rows.length; r++) {
    const row = rows[r] ?? [];
    const first = txt(row[0]);
    if (!first) continue;
    const cat = first.match(/^Category\s*:\s*(.+)$/i);
    if (cat) { category = cat[1].trim(); continue; }
    // Footers. Pastel's own grand total is kept to prove nothing was missed.
    if (/^(Grand\s+)?Totals?\s*:/i.test(first)) { const t = num(row[iTotal]); if (t !== null) footerTotal = t; continue; }
    if (/^(Total\b|Percentage\b)/i.test(first)) continue;
    const m = first.match(/^([A-Z0-9]{2,10})\s+-\s+(.+)$/i);
    if (!m) { warnings.push(`Row ${r + 1}: "${first}" is not "CODE - NAME" — skipped.`); continue; }
    const code = m[1].toUpperCase();
    const totalDue = num(row[iTotal]);
    if (totalDue === null) { warnings.push(`${code}: no Total Due — skipped.`); continue; }
    if (seen.has(code)) warnings.push(`${code} appears more than once in the export.`);
    seen.add(code);
    const pr: PastelRow = { code, name: m[2].trim().replace(/\s+/g, ' '), category, totalDue: round2(totalDue) };
    if (hasBuckets) {
      pr.buckets = {
        days120: round2(num(row[iB.days120]) ?? 0),
        days90: round2(num(row[iB.days90]) ?? 0),
        days60: round2(num(row[iB.days60]) ?? 0),
        days30: round2(num(row[iB.days30]) ?? 0),
        current: round2(num(row[iB.current]) ?? 0),
      };
    }
    out.push(pr);
  }
  const total = round2(out.reduce((a, x) => a + x.totalDue, 0));
  if (footerTotal !== null && Math.abs(round2(footerTotal) - total) >= 0.01) {
    warnings.push(`Pastel's grand total is ${round2(footerTotal)} but the rows read add up to ${total} — some rows may not have been read.`);
  }
  return { title, rows: out, total, hasBuckets, warnings };
}

/* ------------------------------------------------------------------
   Comparison
   ------------------------------------------------------------------ */

export type DiffKind =
  /** Both ledgers know the pupil and agree. */
  | 'agree'
  /** Both know the pupil; the balances differ. */
  | 'differs'
  /** In Pastel, and no pupil carries the code — matched by name instead. */
  | 'name_only'
  /** In Pastel, and the portal cannot find the pupil at all. */
  | 'missing_in_portal'
  /** The portal shows a balance for a pupil Pastel does not list. */
  | 'missing_in_pastel';

export interface CompareStudent {
  id: string;
  name: string;
  pastelCode?: string;
  className?: string;
  status?: string;
}

export interface DiffRow {
  kind: DiffKind;
  code: string;
  pastelName: string;
  studentId: string | null;
  portalName: string;
  className: string;
  category: string;
  pastel: number;
  portal: number;
  /** portal − pastel. Positive: the portal says the pupil owes more. */
  difference: number;
}

export interface ComparisonSummary {
  rows: DiffRow[];
  counts: Record<DiffKind, number>;
  pastelTotal: number;
  /** Portal balance of every pupil that took part in the comparison. */
  portalTotal: number;
  /** Σ |difference| — the size of the disagreement, not its net. */
  absoluteDifference: number;
  /** Codes carried by more than one pupil — a data fault to fix first. */
  duplicateCodes: string[];
}

/** Differences smaller than this are rounding, not disagreement. */
export const TOLERANCE = 0.5;

function normName(s: string): string {
  return s.toUpperCase().replace(/[^A-Z\s]/g, ' ').split(/\s+/).filter(Boolean).sort().join(' ');
}

/**
 * Set the export beside the portal. `balanceOf` is the portal balance by
 * student id (accountStandings), absent meaning no account = 0.
 */
export function compareAgeAnalysis(
  pastel: PastelRow[],
  students: CompareStudent[],
  balanceOf: (studentId: string) => number,
): ComparisonSummary {
  const byCode = new Map<string, CompareStudent[]>();
  const byName = new Map<string, CompareStudent[]>();
  for (const s of students) {
    const code = (s.pastelCode ?? '').trim().toUpperCase();
    if (code) (byCode.get(code) ?? byCode.set(code, []).get(code)!).push(s);
    const n = normName(s.name);
    if (n) (byName.get(n) ?? byName.set(n, []).get(n)!).push(s);
  }
  const duplicateCodes = Array.from(byCode.entries()).filter(([, v]) => v.length > 1).map(([k]) => k).sort();

  const rows: DiffRow[] = [];
  const used = new Set<string>();
  for (const p of pastel) {
    let st = byCode.get(p.code)?.[0];
    let kind: DiffKind | null = null;
    if (!st) {
      const cands = (byName.get(normName(p.name)) ?? []).filter((s) => !used.has(s.id) && !s.pastelCode);
      if (cands.length === 1) { st = cands[0]; kind = 'name_only'; }
    }
    if (!st) {
      // The Detailed Ledger lists every account, settled and long-gone ones
      // too. One with nothing on it and no pupil is not a difference.
      if (Math.abs(p.totalDue) < TOLERANCE) continue;
      rows.push({
        kind: 'missing_in_portal', code: p.code, pastelName: p.name, studentId: null, portalName: '',
        className: '', category: p.category, pastel: p.totalDue, portal: 0, difference: round2(-p.totalDue),
      });
      continue;
    }
    used.add(st.id);
    const portal = round2(balanceOf(st.id));
    const difference = round2(portal - p.totalDue);
    rows.push({
      kind: kind ?? (Math.abs(difference) < TOLERANCE ? 'agree' : 'differs'),
      code: p.code, pastelName: p.name, studentId: st.id, portalName: st.name,
      className: st.className ?? '', category: p.category, pastel: p.totalDue, portal, difference,
    });
  }
  // The age analysis left settled accounts out, and a pupil with no Pastel
  // account at all is only news when the portal says they owe or hold
  // something — so a nil portal balance is never listed.
  for (const s of students) {
    if (used.has(s.id)) continue;
    const portal = round2(balanceOf(s.id));
    if (Math.abs(portal) < TOLERANCE) continue;
    rows.push({
      kind: 'missing_in_pastel', code: s.pastelCode ?? '', pastelName: '', studentId: s.id, portalName: s.name,
      className: s.className ?? '', category: '', pastel: 0, portal, difference: portal,
    });
  }

  const counts: Record<DiffKind, number> = { agree: 0, differs: 0, name_only: 0, missing_in_portal: 0, missing_in_pastel: 0 };
  for (const r of rows) counts[r.kind] += 1;
  return {
    rows,
    counts,
    pastelTotal: round2(pastel.reduce((a, p) => a + p.totalDue, 0)),
    portalTotal: round2(rows.reduce((a, r) => a + r.portal, 0)),
    absoluteDifference: round2(rows.filter((r) => r.kind !== 'agree').reduce((a, r) => a + Math.abs(r.difference), 0)),
    duplicateCodes,
  };
}
